import { useState, useEffect } from 'react';
import notificationServices from 'services/notificationServices';
import { checkEnableParseJSON } from '../helper/helper';
import { ContactInfo } from './types';

// Helper function to check if notification is enabled for receive type
const checkNotificationEnabled = (
  emailNotificationArray: string | null,
  notiType: 'email' | 'sms'
): boolean => {
  if (!emailNotificationArray) return false;

  try {
    const parsed = checkEnableParseJSON(emailNotificationArray, []);
    if (!Array.isArray(parsed)) return false;

    // Check if there's an entry with noti_type + type="receive" + is_enabled=true
    return parsed.some(
      (item: any) =>
        item.noti_type === notiType && item.type === 'receive' && item.is_enabled === true
    );
  } catch (error) {
    console.error('Error parsing email_notification_array:', error);
    return false;
  }
};

export const useContactInfo = (userType: string, visible: boolean) => {
  const [emailList, setEmailList] = useState<string[]>([]);
  const [phoneList, setPhoneList] = useState<string[]>([]);
  const [emailContactInfo, setEmailContactInfo] = useState<ContactInfo[]>([]);
  const [phoneContactInfo, setPhoneContactInfo] = useState<ContactInfo[]>([]);
  const [contactLoading, setContactLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!visible) return;

    const getPartnersContactInfo = () => {
      if (!userType) {
        setContactLoading(false);
        return;
      }

      setContactLoading(true);
      const role = userType.toLowerCase();
      notificationServices
        .getPartnersContactInfo(role)
        .then((res: any) => {
          console.log('🚀 ~ getPartnersContactInfo ~ res:', res);
          const contacts = res?.data || [];
          const formattedEmails: string[] = [];
          const formattedPhones: string[] = [];
          const emailContacts: ContactInfo[] = [];
          const phoneContacts: ContactInfo[] = [];

          if (role === 'student') {
            // For student role: response has parent_type, email, phone_number, first_name, last_name, partner_role
            contacts.forEach((item: any) => {
              const name = `${item.first_name || ''} ${item.middle_name || ''} ${
                item.last_name || ''
              }`.trim();
              const itemRole = item.partner_role || item.parent_type || '';
              // Only display name with role, email/phone will be hidden but stored after ":"
              const displayName = name ? `${name}${itemRole ? ` (${itemRole})` : ''}` : '';

              // Check notification status
              const emailNotiEnabled = checkNotificationEnabled(
                item.email_notification_array,
                'email'
              );
              const smsNotiEnabled = checkNotificationEnabled(item.email_notification_array, 'sms');

              // Process email - format: "displayName: email" (email hidden in UI)
              if (item.email && item.email.trim() && item.email.toLowerCase() !== 'ronrx') {
                const formattedValue = displayName
                  ? `${displayName}: ${item.email}`
                  : `Unknown: ${item.email}`;
                formattedEmails.push(formattedValue);
                emailContacts.push({
                  formattedValue,
                  email: item.email,
                  emailNotificationEnabled: emailNotiEnabled,
                  smsNotificationEnabled: smsNotiEnabled,
                });
              }

              // Process phone - format: "displayName: phone" (phone hidden in UI)
              if (
                item.phone_number &&
                item.phone_number.trim() &&
                item.phone_number.toLowerCase() !== 'ronrx'
              ) {
                const phoneWithCode = item.phone_number;
                const formattedValue = displayName
                  ? `${displayName}: ${phoneWithCode}`
                  : `Unknown: ${phoneWithCode}`;
                formattedPhones.push(formattedValue);
                phoneContacts.push({
                  formattedValue,
                  phone: phoneWithCode,
                  emailNotificationEnabled: emailNotiEnabled,
                  smsNotificationEnabled: smsNotiEnabled,
                });
              }
            });
          } else {
            // For counselor/parent role: response has student_* and parent_* fields
            contacts.forEach((item: any) => {
              const studentName = `${item.student_first_name || ''} ${
                item.student_middle_name || ''
              } ${item.student_last_name || ''}`.trim();

              // Check notification status for student
              const studentEmailNotiEnabled = checkNotificationEnabled(
                item.email_notification_array,
                'email'
              );
              const studentSmsNotiEnabled = checkNotificationEnabled(
                item.email_notification_array,
                'sms'
              );

              // Process student email - show student name with "student" label
              if (item.student_email && item.student_email.trim()) {
                const displayName = studentName ? `${studentName} (student)` : 'student';
                const formattedValue = `${displayName}: ${item.student_email}`;
                formattedEmails.push(formattedValue);
                emailContacts.push({
                  formattedValue,
                  email: item.student_email,
                  emailNotificationEnabled: studentEmailNotiEnabled,
                  smsNotificationEnabled: studentSmsNotiEnabled,
                });
              }

              // Process student phone
              if (item.student_phone_number && item.student_phone_number.trim()) {
                const displayName = studentName ? `${studentName} (student)` : 'student';
                const formattedValue = `${displayName}: ${item.student_phone_number}`;
                formattedPhones.push(formattedValue);
                phoneContacts.push({
                  formattedValue,
                  phone: item.student_phone_number,
                  emailNotificationEnabled: studentEmailNotiEnabled,
                  smsNotificationEnabled: studentSmsNotiEnabled,
                });
              }

              // Process parent email - show parent name with role and student info
              if (item.parent_email && item.parent_email.trim()) {
                const parentName = `${item.parent_first_name || ''} ${
                  item.parent_middle_name || ''
                } ${item.parent_last_name || ''}`.trim();
                const itemRole = item.parent_user_role || item.partner_role || 'parent';
                // Format: "Parent Name (role) - Student Name"
                const displayName = parentName
                  ? `${parentName} (${itemRole})${studentName ? ` - ${studentName}` : ''}`
                  : `${itemRole}${studentName ? ` - ${studentName}` : ''}`;
                const formattedValue = `${displayName}: ${item.parent_email}`;
                formattedEmails.push(formattedValue);
                // For parent, we don't have separate notification array, so default to false
                emailContacts.push({
                  formattedValue,
                  email: item.parent_email,
                  emailNotificationEnabled: false,
                  smsNotificationEnabled: false,
                });
              }

              // Process parent phone
              if (item.parent_phone_number && item.parent_phone_number.trim()) {
                const parentName = `${item.parent_first_name || ''} ${
                  item.parent_middle_name || ''
                } ${item.parent_last_name || ''}`.trim();
                const itemRole = item.parent_user_role || item.partner_role || 'parent';
                // Format: "Parent Name (role) - Student Name"
                const displayName = parentName
                  ? `${parentName} (${itemRole})${studentName ? ` - ${studentName}` : ''}`
                  : `${itemRole}${studentName ? ` - ${studentName}` : ''}`;
                const formattedValue = `${displayName}: ${item.parent_phone_number}`;
                formattedPhones.push(formattedValue);
                // For parent, we don't have separate notification array, so default to false
                phoneContacts.push({
                  formattedValue,
                  phone: item.parent_phone_number,
                  emailNotificationEnabled: false,
                  smsNotificationEnabled: false,
                });
              }
            });
          }

          // Remove duplicates while preserving notification status
          const uniqueEmailContacts = emailContacts.reduce((acc: ContactInfo[], current) => {
            const existing = acc.find((item) => item.email === current.email);
            if (!existing) {
              acc.push(current);
            }
            return acc;
          }, []);

          const uniquePhoneContacts = phoneContacts.reduce((acc: ContactInfo[], current) => {
            const existing = acc.find((item) => item.phone === current.phone);
            if (!existing) {
              acc.push(current);
            }
            return acc;
          }, []);

          setEmailList([...new Set(formattedEmails)]);
          setPhoneList([...new Set(formattedPhones)]);
          setEmailContactInfo(uniqueEmailContacts);
          setPhoneContactInfo(uniquePhoneContacts);

          setContactLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching partners contact info:', error);
          setEmailList([]);
          setPhoneList([]);
          setEmailContactInfo([]);
          setPhoneContactInfo([]);
          setContactLoading(false);
        });
    };

    getPartnersContactInfo();
  }, [userType, visible]);

  // Helper function to check if SMS notification is enabled for a phone number
  const isSmsNotificationEnabled = (phone: string): boolean => {
    const normalizedPhone = phone.replace(/[^\d+]/g, '');
    const contact = phoneContactInfo.find((item) => {
      const itemPhone = item.phone || '';
      return itemPhone.replace(/[^\d+]/g, '') === normalizedPhone;
    });
    return contact?.smsNotificationEnabled ?? false;
  };

  // Helper function to check if Email notification is enabled for an email address
  const isEmailNotificationEnabled = (email: string): boolean => {
    const normalizedEmail = email.toLowerCase().trim();
    const contact = emailContactInfo.find((item) => {
      const itemEmail = item.email || '';
      return itemEmail.toLowerCase().trim() === normalizedEmail;
    });
    return contact?.emailNotificationEnabled ?? false;
  };

  return {
    emailList,
    phoneList,
    emailContactInfo,
    phoneContactInfo,
    contactLoading,
    isSmsNotificationEnabled,
    isEmailNotificationEnabled,
  };
};

